import axios from "axios";
import type { AxiosResponse } from "axios";
import type {
  LocationInfo,
  ProviderDescription,
  AvailableTime,
  ExistingPatientAvailableTimes,
  LeadRecord,
  PatientRecord,
  PatientLookupResult,
  NewPatientTempNote,
  Success,
  ApiSuccess,
  InsertNewPatientWithApptResult,
  GetProvidersQueryObj,
  PatientLookupQueryObj,
  GetNewPatientApptTimesQueryObj,
  GetExistingPatientApptTimesQueryObj,
  InsertNewPatientQueryObj,
  InsertExistingPatientQueryObj,
  UpdateNewPatientQueryObj,
  UpdateExistingPatientQueryObj,
  UpdateNewPatientApptQueryObj,
  UpdateExistingPatientApptQueryObj,
  InsertNewPatientWithApptQueryObj,
  InsertExistingPatientWithApptQueryObj,
  InsertLeadNoteQueryObj,
  GetLeadNotesQueryObj,
  DeleteLeadNoteQueryObj,
  SendEmailQueryObj,
} from "@/types/booking";

const client = axios.create({
  baseURL: "/api/booking",
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// GET endpoints
// ---------------------------------------------------------------------------

function getLocationInfo(locationCode: string): Promise<AxiosResponse<LocationInfo>> {
  return client.get(`/location-info/${encodeURIComponent(locationCode)}`);
}

function getAlertEmails(locationCode: string): Promise<AxiosResponse<string[]>> {
  return client.get(`/alert-emails/${encodeURIComponent(locationCode)}`);
}

function getLocationEmail(locationCode: string): Promise<AxiosResponse<string>> {
  return client.get(`/location-email/${encodeURIComponent(locationCode)}`);
}

function getCategories(locationCode: string): Promise<AxiosResponse<string[]>> {
  return client.get(`/categories/${encodeURIComponent(locationCode)}`);
}

function getProvidersByLocation(locationCode: string): Promise<AxiosResponse<ProviderDescription[]>> {
  return client.get(`/providers/${encodeURIComponent(locationCode)}`);
}

function getLead(locationCode: string, leadId: string): Promise<AxiosResponse<LeadRecord>> {
  return client.get(`/lead/${encodeURIComponent(locationCode)}/${encodeURIComponent(leadId)}`);
}

function getPatientRecord(locationCode: string, recordId: string): Promise<AxiosResponse<PatientRecord>> {
  return client.get(`/patient-record/${encodeURIComponent(locationCode)}/${encodeURIComponent(recordId)}`);
}

// ---------------------------------------------------------------------------
// POST endpoints
// ---------------------------------------------------------------------------

function getProviders(query: GetProvidersQueryObj): Promise<AxiosResponse<ProviderDescription[]>> {
  return client.post("/providers", query);
}

function patientLookup(query: PatientLookupQueryObj): Promise<AxiosResponse<PatientLookupResult>> {
  return client.post("/patient-lookup", query);
}

function getNewPatientApptTimes(query: GetNewPatientApptTimesQueryObj): Promise<AxiosResponse<AvailableTime[]>> {
  return client.post("/new-patient-appt-times", query);
}

function getExistingPatientApptTimes(query: GetExistingPatientApptTimesQueryObj): Promise<AxiosResponse<ExistingPatientAvailableTimes>> {
  return client.post("/existing-patient-appt-times", query);
}

function insertNewPatient(query: InsertNewPatientQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/insert-new-patient", query);
}

function insertLead(query: InsertNewPatientQueryObj): Promise<AxiosResponse<ApiSuccess>> {
  return client.post("/insert-lead", query);
}

function insertLeadNote(query: InsertLeadNoteQueryObj): Promise<AxiosResponse<ApiSuccess>> {
  return client.post("/insert-lead-note", query);
}

function getLeadNotes(query: GetLeadNotesQueryObj): Promise<AxiosResponse<NewPatientTempNote[]>> {
  return client.post("/get-lead-notes", query);
}

function deleteLeadNote(query: DeleteLeadNoteQueryObj): Promise<AxiosResponse<ApiSuccess>> {
  return client.post("/delete-lead-note", query);
}

function insertExistingPatient(query: InsertExistingPatientQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/insert-existing-patient", query);
}

function updateNewPatient(query: UpdateNewPatientQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/update-new-patient", query);
}

function updateExistingPatient(query: UpdateExistingPatientQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/update-existing-patient", query);
}

function updateNewPatientAppt(query: UpdateNewPatientApptQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/update-new-patient-appt", query);
}

function updateExistingPatientAppt(query: UpdateExistingPatientApptQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/update-existing-patient-appt", query);
}

function insertNewPatientWithAppt(query: InsertNewPatientWithApptQueryObj): Promise<AxiosResponse<InsertNewPatientWithApptResult>> {
  return client.post("/insert-new-patient-with-appt", query);
}

function insertExistingPatientWithAppt(query: InsertExistingPatientWithApptQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/insert-existing-patient-with-appt", query);
}

function sendEmail(query: SendEmailQueryObj): Promise<AxiosResponse<Success>> {
  return client.post("/send-email", query);
}

// ---------------------------------------------------------------------------
// Exported client
// ---------------------------------------------------------------------------

export const bookingClient = {
  getLocationInfo,
  getAlertEmails,
  getLocationEmail,
  getCategories,
  getProvidersByLocation,
  getLead,
  getPatientRecord,
  getProviders,
  patientLookup,
  getNewPatientApptTimes,
  getExistingPatientApptTimes,
  insertNewPatient,
  insertLead,
  insertLeadNote,
  getLeadNotes,
  deleteLeadNote,
  insertExistingPatient,
  updateNewPatient,
  updateExistingPatient,
  updateNewPatientAppt,
  updateExistingPatientAppt,
  insertNewPatientWithAppt,
  insertExistingPatientWithAppt,
  sendEmail,
};
